import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { LiveData, Service } from '@toeverything/infra';

import type { GlobalStateService } from '../../storage';
import type { ReasoningModeId } from './models';

const AI_REASONING_KEY = 'AIReasoning';
const AI_REASONING_EFFORT_KEY = 'AIReasoningEffort';
const DEFAULT_REASONING_EFFORT = 'medium' satisfies ReasoningModeId;

export type ReasoningEffortLevel = ReasoningModeId;

function isActiveReasoningEffort(
  effort: ReasoningEffortLevel | undefined
): boolean {
  return !!effort && effort !== 'off' && effort !== 'auto';
}

export class AIReasoningService extends Service {
  constructor(private readonly globalStateService: GlobalStateService) {
    super();

    const { signal: enabled, cleanup: enabledCleanup } =
      createSignalFromObservable<boolean | undefined>(
        this._enabled$,
        undefined
      );
    this.enabled = enabled;
    this.disposables.push(enabledCleanup);

    const { signal: effort, cleanup: effortCleanup } =
      createSignalFromObservable<ReasoningEffortLevel | undefined>(
        this._effort$,
        undefined
      );
    this.effort = effort;
    this.disposables.push(effortCleanup);
  }

  enabled: Signal<boolean | undefined>;
  effort: Signal<ReasoningEffortLevel | undefined>;

  private readonly _enabled$ = LiveData.from(
    this.globalStateService.globalState.watch<boolean>(AI_REASONING_KEY),
    undefined
  );

  private readonly _effort$ = LiveData.from(
    this.globalStateService.globalState.watch<ReasoningEffortLevel>(
      AI_REASONING_EFFORT_KEY
    ),
    undefined
  );

  setEnabled = (enabled: boolean) => {
    if (enabled && !isActiveReasoningEffort(this.effort.value)) {
      this.setEffort(DEFAULT_REASONING_EFFORT);
    }
    // Update signal synchronously so UI reads the new value immediately
    // (GlobalState → Observable → Signal propagation is async)
    this.enabled.value = enabled;
    this.globalStateService.globalState.set(AI_REASONING_KEY, enabled);
  };

  setEffort = (effort: ReasoningEffortLevel) => {
    this.effort.value = effort;
    this.globalStateService.globalState.set(AI_REASONING_EFFORT_KEY, effort);
  };

  /**
   * Get effective effort level (default: medium)
   */
  get effectiveEffort(): ReasoningEffortLevel {
    return this.effort.value ?? DEFAULT_REASONING_EFFORT;
  }
}
